import {Avatar, Flag} from 'platform/components';
import {HStack, Icon, Show, Text, ThemeIconKey, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CommonAddressResponseBody,
  CustomerAdvancedSearchResultContactResponseBody,
  CustomerAdvancedSearchResultResponseBody,
} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {TestIdProps} from 'shared/web';

import {useAddress} from '../../../hooks/useAddress';
import {getNaturalPersonFullName} from '../../../utils/getNaturalPersonFullName';
import {getMatchTextColor} from '../utils/getMatchTextColor';
import {joinInfoLine} from '../utils/joinInfoLine';
import {ResultRowShell} from './ResultRowShell';
import {RowContactAside} from './RowContactAside';

interface BusinessContactResultRowProps extends TestIdProps {
  customer: CustomerAdvancedSearchResultResponseBody;
  contact: CustomerAdvancedSearchResultContactResponseBody;
  onSelect: (customer: CustomerAdvancedSearchResultResponseBody, contactId: string) => void;
}

interface ParentInfo {
  icon: ThemeIconKey;
  name: string;
  phone: string | null;
  email: string | null;
  address: CommonAddressResponseBody | null;
}

const getParentInfo = (customer: CustomerAdvancedSearchResultResponseBody): ParentInfo | null =>
  match(customer.discriminator)
    .with('BUSINESS', (): ParentInfo | null =>
      customer.tradeName
        ? {
            icon: 'social/domain',
            name: customer.tradeName,
            phone: customer.foundingPerson?.phone ?? null,
            email: customer.foundingPerson?.email ?? null,
            address: customer.businessAddress,
          }
        : null
    )
    .with('NATURAL_PERSON', (): ParentInfo | null => {
      const fullName = getNaturalPersonFullName(customer.foundingPerson);
      return fullName
        ? {
            icon: 'social/person',
            name: fullName,
            phone: customer.foundingPerson?.phone ?? null,
            email: customer.foundingPerson?.email ?? null,
            address: customer.foundingPerson?.address ?? null,
          }
        : null;
    })
    .exhaustive();

export function BusinessContactResultRow(props: BusinessContactResultRowProps) {
  const {composeAddress} = useAddress();

  const parent = getParentInfo(props.customer);
  const parentInfoLine = parent
    ? joinInfoLine([parent.phone, parent.email, composeAddress(parent.address)])
    : '';

  return (
    <ResultRowShell
      avatar={<Avatar icon="action/account_box" size="small" />}
      aside={
        <RowContactAside
          phone={props.contact.phone}
          phoneMatchType={props.contact.phoneMatchType}
          email={props.contact.email}
        />
      }
      onSelect={() => props.onSelect(props.customer, props.contact.id)}
      data-testid={props['data-testid']}
    >
      <HStack spacing={2} align="center">
        <Text size="small" color={getMatchTextColor(props.contact.firstNameMatchType)} alternative>
          {props.contact.firstName}
        </Text>
        <Show when={isNotNilOrEmpty(props.contact.lastName)}>
          <Text size="small" color={getMatchTextColor(props.contact.lastNameMatchType)} alternative>
            {props.contact.lastName}
          </Text>
        </Show>
        <Flag
          label={i18n.t('entity.customer.labels.contactPerson')}
          colorScheme="orange"
          size="small"
        />
      </HStack>
      <Show when={parent}>
        {(parent) => (
          <VStack spacing={0}>
            <HStack spacing={1} align="center">
              <Icon value={parent.icon} size={3} color="text.tertiary" />
              <Text size="xSmall" color="tertiary" noWrap>
                {parent.name}
              </Text>
            </HStack>
            <Show when={isNotNilOrEmpty(parentInfoLine)}>
              <Text size="xSmall" color="tertiary">
                {parentInfoLine}
              </Text>
            </Show>
          </VStack>
        )}
      </Show>
    </ResultRowShell>
  );
}
